import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { mdiChevronDown, mdiDotsVertical, mdiLightningBolt } from '@mdi/js';
import Icon from '@mdi/react';
import { BaseButton, ISelectOption, PopoverItem, Spinner, Toggle } from '../base';
import { BadgeLabeledValue } from '../badge';
import { SelectField } from '../base/form';
import { FormField } from '../form-field';
import { DemoModeBlockedError } from '../../services/auth/demo-mode.utils';
import {
    ClaimRulePair,
    ClaimSuppressedRules,
    CoderRuleCodingSource,
    expandRestorePairs,
    expandSuppressPairs,
    extractCodingRules,
    formatCoderRulesChipValue,
    mapWithConcurrency,
    mergeClaimCoderRules,
    parseSelectedRuleNumbers,
    parseSuppressedCoderRules,
    partitionPairResults,
    PairAttemptResult,
    runClaimRulePairs,
    SelectionCoderRule,
    summarizeSelectionRules,
    SuppressedCoderRule,
    toSelectionRuleSelectOptions,
    withoutSuppressedRule,
    withSuppressedRule,
} from './coder-rule-suppression.util';
import * as S from './coder-rule-suppression.styles';

export interface CoderRuleSuppressionApi {
    listSuppressed: (claimId: string) => Promise<unknown>;
    /** Codings for several claims at once — the batch dialog reads its rule list out of their notes. */
    listCodings: (claimIds: string[]) => Promise<CoderRuleCodingSource[]>;
    suppress: (claimId: string, ruleNumber: number) => Promise<void>;
    unsuppress: (claimId: string, ruleNumber: number) => Promise<void>;
}

const SuppressSchema = Yup.object().shape({
    ruleNumbers: Yup.array().of(Yup.mixed()).min(1, 'Select at least one coder rule'),
});

function isDemoBlocked(error: unknown): boolean {
    return error instanceof DemoModeBlockedError || (error instanceof Error && error.name === 'DemoModeBlockedError');
}

function loadFailureCopy(error: unknown): string {
    if (isDemoBlocked(error)) {
        return 'Coder rule suppression is unavailable in demo mode.';
    }
    return 'Coder rules could not be loaded. Try again.';
}

function actionFailureCopy(error: unknown): string {
    if (isDemoBlocked(error)) {
        return 'Coder rule suppression is unavailable in demo mode.';
    }
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return 'The coder rule could not be updated. Try again.';
}

interface SuppressFormValues {
    ruleNumbers: string[];
}

const CoderRuleSelect: React.FC<{
    name: string;
    options: ISelectOption[];
    disabled?: boolean;
    placeholder?: string;
}> = ({ name, options, disabled, placeholder }) => (
    <FormField label="Coder rules" required>
        <Field
            name={name}
            component={SelectField}
            options={options}
            isMulti={true}
            closeMenuOnSelect={false}
            isDisabled={disabled}
            placeholder={placeholder ?? 'Select one or more rules'}
        />
    </FormField>
);

const DropdownPanel: React.FC<{
    children?: React.ReactNode;
    closeMenu?: () => void;
    pinned?: boolean;
    selectable?: boolean;
}> = ({ children }) => <S.Dropdown>{children}</S.Dropdown>;

/**
 * Loaded state carries the claim it was read from. A host page can keep this component mounted while
 * `claimId` changes, and untagged state would let one claim's rules render — and be toggled — against
 * another claim's id, suppressing a rule on the wrong claim.
 */
interface LoadedSuppression {
    claimId: string;
    rules?: SuppressedCoderRule[];
    error?: string;
}

interface BusyRule {
    claimId: string;
    ruleNumber: number;
}

export const ClaimCoderRuleSuppression: React.FC<{
    claimId: string;
    api: CoderRuleSuppressionApi;
    codings?: CoderRuleCodingSource[];
}> = ({ claimId, api, codings }) => {
    const [loaded, setLoaded] = useState<LoadedSuppression | undefined>(undefined);
    // Every rule with a write in flight, not just the newest. A single slot would re-enable a row the
    // moment another rule was toggled, and a second click on a still-writing rule sends a second write
    // whose ordering against the first nothing guarantees — the claim could settle on either.
    const [busyRules, setBusyRules] = useState<BusyRule[]>([]);
    const [open, setOpen] = useState(false);
    // Rows are independent, so several writes can be in flight at once, while a read-back answers with
    // the whole list. A read-back issued before another rule's write therefore describes a claim that
    // no longer exists: applying it both loses that write and reverts its row. Two counters keep the
    // list honest — writes still outstanding, and the number of the most recent toggle.
    const pendingToggles = useRef(0);
    const toggleGeneration = useRef(0);

    const current = loaded?.claimId === claimId ? loaded : undefined;
    const suppressed = current?.rules;
    const loadError = current?.error;
    const busyRuleNumbers = useMemo(
        () => new Set(busyRules.filter(entry => entry.claimId === claimId).map(entry => entry.ruleNumber)),
        [busyRules, claimId],
    );

    const fetchSuppressed = useCallback(
        async (id: string) => parseSuppressedCoderRules(await api.listSuppressed(id)),
        [api],
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const rules = await fetchSuppressed(claimId);
                if (!cancelled) {
                    setLoaded({ claimId, rules });
                }
            } catch (error) {
                if (!cancelled) {
                    setLoaded({ claimId, error: loadFailureCopy(error) });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [claimId, fetchSuppressed]);

    const handleOpenChange = (nextOpen: boolean): void => {
        setOpen(nextOpen);
    };

    const codingRules = useMemo(() => extractCodingRules(codings ?? []), [codings]);
    const rules = useMemo(
        () => (suppressed === undefined ? undefined : mergeClaimCoderRules(codingRules, suppressed)),
        [codingRules, suppressed],
    );

    // The toggle asks "suppress this rule?", so on means suppressed and every rule starts off —
    // suppression is opt-in, never something a provider inherits from a coder run.
    const handleToggle = async (rule: SuppressedCoderRule, suppress: boolean): Promise<void> => {
        // The claim this toggle was aimed at, captured before any await: the host page may switch
        // claims while the request is in flight, and the write must still land on the claim whose
        // rules the user was looking at.
        const targetClaimId = claimId;
        setBusyRules(previous => [...previous, { claimId: targetClaimId, ruleNumber: rule.ruleNumber }]);
        // Rules toggle independently, so both the optimistic write and its rollback touch only this
        // rule — a whole-list snapshot would undo a toggle another row made in the meantime.
        const applyToggle = (
            previous: SuppressedCoderRule[] | undefined,
            isSuppressed: boolean,
        ): SuppressedCoderRule[] =>
            isSuppressed
                ? withSuppressedRule(previous ?? [], rule)
                : withoutSuppressedRule(previous ?? [], rule.ruleNumber);

        setLoaded(previous =>
            previous?.claimId === targetClaimId
                ? { claimId: targetClaimId, rules: applyToggle(previous.rules, suppress) }
                : previous,
        );

        toggleGeneration.current += 1;
        pendingToggles.current += 1;
        let written = false;
        try {
            if (suppress) {
                await api.suppress(targetClaimId, rule.ruleNumber);
            } else {
                await api.unsuppress(targetClaimId, rule.ruleNumber);
            }
            written = true;
        } catch (error) {
            setLoaded(previous =>
                previous?.claimId === targetClaimId
                    ? {
                          claimId: targetClaimId,
                          rules: applyToggle(previous.rules, !suppress),
                          error: actionFailureCopy(error),
                      }
                    : previous,
            );
        } finally {
            pendingToggles.current -= 1;
            setBusyRules(previous =>
                previous.filter(entry => entry.claimId !== targetClaimId || entry.ruleNumber !== rule.ruleNumber),
            );
        }

        // Read the list back once, from whichever toggle settles last: by then every write has landed,
        // so the answer describes all of them. A toggle that finishes while another is still writing
        // leaves the read-back to that one.
        if (!written || pendingToggles.current > 0) {
            return;
        }
        const issuedAt = toggleGeneration.current;
        try {
            const refreshed = await fetchSuppressed(targetClaimId);
            // A toggle started after this read went out, so its optimistic row is newer than anything
            // the response can know about.
            if (issuedAt !== toggleGeneration.current) {
                return;
            }
            setLoaded(previous =>
                previous?.claimId === targetClaimId ? { claimId: targetClaimId, rules: refreshed } : previous,
            );
        } catch {
            // The write succeeded and only the read-back failed. The optimistic row already states
            // what the server was told, so reverting it or reporting a failure would both be untrue.
        }
    };

    const suppressedCount = suppressed?.length;
    const chipTone: S.HeaderChipTone = suppressedCount ? 'attention' : 'neutral';

    return (
        <S.HeaderChipPopover
            align="right"
            verticalAlign="bottom"
            onOpenChange={handleOpenChange}
            renderButton={() => (
                <S.HeaderChip type="button" tone={chipTone} aria-expanded={open} aria-haspopup="listbox">
                    <S.HeaderChipMain>
                        <S.HeaderChipIcon path={mdiLightningBolt} />
                        <BadgeLabeledValue prefix="Coder rules:" value={formatCoderRulesChipValue(suppressedCount)} />
                    </S.HeaderChipMain>
                    <S.HeaderChipAction>
                        <S.HeaderChipChevron path={mdiChevronDown} />
                    </S.HeaderChipAction>
                </S.HeaderChip>
            )}
        >
            <DropdownPanel>
                <S.DropdownHeader>
                    <S.DropdownTitle>Coder rules</S.DropdownTitle>
                    {suppressed !== undefined && <S.DropdownCounts>{suppressed.length} suppressed</S.DropdownCounts>}
                </S.DropdownHeader>
                {loadError && <S.DropdownError>{loadError}</S.DropdownError>}
                {open && suppressed === undefined && !loadError && (
                    <S.LoadingRow>
                        <Spinner size="16px" />
                    </S.LoadingRow>
                )}
                {rules !== undefined && rules.length === 0 && (
                    <S.RuleRow>
                        <S.RuleDescription>No coder rules ran on this claim.</S.RuleDescription>
                    </S.RuleRow>
                )}
                {rules !== undefined && rules.length > 0 && (
                    <S.RuleList>
                        {rules.map(rule => (
                            <S.RuleRow key={rule.ruleNumber}>
                                <Toggle
                                    value={rule.suppressed}
                                    disabled={busyRuleNumbers.has(rule.ruleNumber)}
                                    onChange={suppress => void handleToggle(rule, suppress)}
                                />
                                <S.RuleCopy>
                                    <S.RuleName>Rule {rule.ruleNumber}</S.RuleName>
                                    {rule.description && <S.RuleDescription>{rule.description}</S.RuleDescription>}
                                </S.RuleCopy>
                            </S.RuleRow>
                        ))}
                    </S.RuleList>
                )}
                <S.DropdownFooter>
                    <S.FooterNote>
                        Turn a rule on to skip it in later coder runs, or off to let it run again. This does not rerun
                        coding or undo previous changes.
                    </S.FooterNote>
                </S.DropdownFooter>
            </DropdownPanel>
        </S.HeaderChipPopover>
    );
};

type BatchAction = 'suppress' | 'restore';

interface BatchActionCopy {
    intro: (claimCount: number) => string;
    placeholder: string;
    submit: string;
    emptyRules: string;
    nothingToDo: string;
    outcomeVerb: string;
}

const BATCH_COPY: Record<BatchAction, BatchActionCopy> = {
    suppress: {
        intro: claimCount =>
            `Skip the selected rules on ${claimCount} claim${claimCount === 1 ? '' : 's'}. ` +
            'Claims that already suppress a rule are left alone.',
        placeholder: 'Select rules to suppress',
        submit: 'Suppress on selected claims',
        emptyRules: 'No coder rules ran on the selected claims.',
        nothingToDo: 'Every selected rule is already suppressed on every selected claim.',
        outcomeVerb: 'suppressed',
    },
    restore: {
        intro: claimCount =>
            `Restore the selected rules on ${claimCount} claim${claimCount === 1 ? '' : 's'}. ` +
            'Only claims that currently suppress a selected rule are updated.',
        placeholder: 'Select suppressed rules to restore',
        submit: 'Restore on selected claims',
        emptyRules: 'None of the selected claims have suppressed coder rules.',
        nothingToDo: 'None of the selected claims suppress a selected rule.',
        outcomeVerb: 'restored',
    },
};

interface BatchSelection {
    perClaim: ClaimSuppressedRules[];
    rules: SelectionCoderRule[];
}

/**
 * What the selection currently suppresses, and — for suppression — which rules its codings applied.
 *
 * Suppressed rules are read per claim because that is the only shape the endpoint offers, while the
 * codings arrive in one grouped, paged request the caller owns.
 */
function useBatchSelection(
    claimIds: string[],
    api: CoderRuleSuppressionApi,
    action: BatchAction,
): { selection?: BatchSelection; error?: string } {
    const [selection, setSelection] = useState<BatchSelection | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [perClaim, codings] = await Promise.all([
                    mapWithConcurrency(claimIds, 5, async claimId => ({
                        claimId,
                        rules: parseSuppressedCoderRules(await api.listSuppressed(claimId)),
                    })),
                    action === 'suppress' ? api.listCodings(claimIds) : Promise.resolve([]),
                ]);
                if (!cancelled) {
                    setSelection({
                        perClaim,
                        rules: summarizeSelectionRules(extractCodingRules(codings), perClaim),
                    });
                    setError(undefined);
                }
            } catch (loadFailure) {
                if (!cancelled) {
                    setSelection(undefined);
                    setError(loadFailureCopy(loadFailure));
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [action, api, claimIds]);

    return { selection, error };
}

interface PairProgress {
    completed: number;
    total: number;
}

const BatchCoderRuleDialog: React.FC<{
    claimIds: string[];
    api: CoderRuleSuppressionApi;
    action: BatchAction;
    onCancel: () => void;
    onComplete?: () => void;
}> = ({ claimIds, api, action, onCancel, onComplete }) => {
    const copy = BATCH_COPY[action];
    const { selection, error: loadError } = useBatchSelection(claimIds, api, action);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState<PairProgress | undefined>(undefined);
    const [results, setResults] = useState<PairAttemptResult[] | undefined>(undefined);

    const partitioned = results ? partitionPairResults(results) : undefined;
    const options = useMemo(
        () => (selection ? toSelectionRuleSelectOptions(selection.rules, claimIds.length) : []),
        [claimIds.length, selection],
    );

    // Retry has to repeat the action that failed. Sharing one runner keeps the retry path from
    // quietly running the opposite endpoint and undoing the batch it was asked to finish.
    const runPairs = async (pairs: ClaimRulePair[]): Promise<void> => {
        setRunning(true);
        setProgress({ completed: 0, total: pairs.length });
        try {
            const nextResults = await runClaimRulePairs(
                pairs,
                action === 'suppress' ? api.suppress : api.unsuppress,
                5,
                (completed, total) => setProgress({ completed, total }),
            );
            setResults(nextResults);
            const { failed } = partitionPairResults(nextResults);
            if (failed.length === 0) {
                onComplete?.();
            }
        } finally {
            setRunning(false);
        }
    };

    const pairsFor = (ruleNumbers: number[]): ClaimRulePair[] => {
        if (!selection) {
            return [];
        }
        return action === 'suppress'
            ? expandSuppressPairs(selection.perClaim, ruleNumbers)
            : expandRestorePairs(selection.perClaim, ruleNumbers);
    };

    const handleRetry = async (): Promise<void> => {
        const failedPairs = (partitioned?.failed ?? []).map(result => result.pair);
        if (failedPairs.length === 0) {
            return;
        }
        await runPairs(failedPairs);
    };

    if (loadError) {
        return (
            <S.DialogBody>
                <S.ErrorText>{loadError}</S.ErrorText>
                <S.Actions>
                    <BaseButton variant="outlined" size="medium" onClick={onCancel}>
                        Close
                    </BaseButton>
                </S.Actions>
            </S.DialogBody>
        );
    }

    if (!selection) {
        return (
            <S.DialogBody>
                <S.LoadingRow>
                    <Spinner size="16px" />
                </S.LoadingRow>
            </S.DialogBody>
        );
    }

    if (results && partitioned) {
        return (
            <S.DialogBody>
                <S.Note>{S.FUTURE_RUN_NOTE}</S.Note>
                <S.Status>
                    {partitioned.succeeded.length} of {results.length} claim/rule pairs {copy.outcomeVerb}.
                </S.Status>
                {partitioned.failed.length > 0 && (
                    <>
                        <S.ErrorText>
                            {partitioned.failed.length} pair{partitioned.failed.length === 1 ? '' : 's'} failed. Retry
                            those without re-running successful pairs.
                        </S.ErrorText>
                        <S.FailureList>
                            {partitioned.failed.map(result => (
                                <S.FailureItem key={`${result.pair.claimId}-${result.pair.ruleNumber}`}>
                                    Claim {result.pair.claimId}, rule {result.pair.ruleNumber}: {result.error}
                                </S.FailureItem>
                            ))}
                        </S.FailureList>
                    </>
                )}
                <S.Actions>
                    <BaseButton variant="outlined" size="medium" onClick={onCancel} disabled={running}>
                        Close
                    </BaseButton>
                    {partitioned.failed.length > 0 && (
                        <BaseButton
                            variant="primary"
                            size="medium"
                            onClick={() => void handleRetry()}
                            disabled={running}
                        >
                            Retry failed
                        </BaseButton>
                    )}
                </S.Actions>
            </S.DialogBody>
        );
    }

    if (options.length === 0) {
        return (
            <S.DialogBody>
                <S.Note>{copy.emptyRules}</S.Note>
                <S.Actions>
                    <BaseButton variant="outlined" size="medium" onClick={onCancel}>
                        Close
                    </BaseButton>
                </S.Actions>
            </S.DialogBody>
        );
    }

    return (
        <S.DialogBody>
            <S.Note>
                {copy.intro(claimIds.length)} {S.FUTURE_RUN_NOTE}
            </S.Note>
            <Formik<SuppressFormValues>
                initialValues={{ ruleNumbers: [] }}
                validationSchema={SuppressSchema}
                onSubmit={async values => {
                    await runPairs(pairsFor(parseSelectedRuleNumbers(values.ruleNumbers)));
                }}
            >
                {({ handleSubmit, isSubmitting, values }) => {
                    const selectedRules = parseSelectedRuleNumbers(values.ruleNumbers);
                    const pairCount = pairsFor(selectedRules).length;
                    const nothingToDo = selectedRules.length > 0 && pairCount === 0;

                    return (
                        <form onSubmit={handleSubmit}>
                            <CoderRuleSelect
                                name="ruleNumbers"
                                options={options}
                                disabled={running || isSubmitting}
                                placeholder={copy.placeholder}
                            />
                            {nothingToDo && <S.Note>{copy.nothingToDo}</S.Note>}
                            {!nothingToDo && pairCount > 0 && !running && (
                                <S.Note>
                                    {pairCount} claim/rule update{pairCount === 1 ? '' : 's'}, sent 5 at a time.
                                </S.Note>
                            )}
                            {running && progress && (
                                <S.Status>
                                    {progress.completed} of {progress.total} done…
                                </S.Status>
                            )}
                            <S.Actions>
                                <BaseButton
                                    variant="outlined"
                                    size="medium"
                                    type="button"
                                    onClick={onCancel}
                                    disabled={running}
                                >
                                    Cancel
                                </BaseButton>
                                <BaseButton
                                    variant="primary"
                                    size="medium"
                                    type="submit"
                                    disabled={running || isSubmitting || nothingToDo}
                                >
                                    {copy.submit}
                                </BaseButton>
                            </S.Actions>
                        </form>
                    );
                }}
            </Formik>
        </S.DialogBody>
    );
};

export const BatchCoderRuleSuppressDialog: React.FC<{
    claimIds: string[];
    api: CoderRuleSuppressionApi;
    onCancel: () => void;
    onComplete?: () => void;
}> = props => <BatchCoderRuleDialog {...props} action="suppress" />;

export const BatchCoderRuleRestoreDialog: React.FC<{
    claimIds: string[];
    api: CoderRuleSuppressionApi;
    onCancel: () => void;
    onComplete?: () => void;
}> = props => <BatchCoderRuleDialog {...props} action="restore" />;

// A plain header row above the two menu actions. `Popover` clones every direct child with
// `closeMenu`/`pinned`/`selectable`/`openSubmenu`/`closeSubmenu` (see base `Popover.renderChildren`);
// declaring — and swallowing — those props here keeps them from landing on the underlying `<div>` as
// unrecognized DOM attributes, the same trick `DropdownPanel` above uses for the same reason.
const FabMenuLabel: React.FC<{
    children?: React.ReactNode;
    closeMenu?: () => void;
    pinned?: boolean;
    selectable?: boolean;
}> = ({ children }) => <S.FabMenuLabel>{children}</S.FabMenuLabel>;

export const BatchCoderRuleSuppressToolbar: React.FC<{
    selectedCount: number;
    onSuppress: () => void;
    onRestore: () => void;
    disabled?: boolean;
}> = ({ selectedCount, onSuppress, onRestore, disabled }) => {
    if (selectedCount === 0) {
        return null;
    }

    return (
        <S.FabContainer>
            <S.FabPopover
                align="right"
                verticalAlign="top"
                renderButton={() => (
                    <BaseButton variant="fab-light" size="large" disabled={disabled}>
                        <Icon path={mdiDotsVertical} size={1} />
                    </BaseButton>
                )}
            >
                <FabMenuLabel>
                    {selectedCount} claim{selectedCount === 1 ? '' : 's'} selected
                </FabMenuLabel>
                <PopoverItem onClick={onRestore} disabled={disabled}>
                    Restore coder rules
                </PopoverItem>
                <PopoverItem onClick={onSuppress} disabled={disabled}>
                    Suppress coder rules
                </PopoverItem>
            </S.FabPopover>
        </S.FabContainer>
    );
};
